import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import ObliqueIcon from '@/components/icons/ObliqueIcon';
import Spinner from '@/components/ui/Spinner';
import { db } from '@/lib/firebase';
import { collection, getDocs, updateDoc, doc } from 'firebase/firestore';
import { logger } from '@/lib/logger';
import { useAuth } from '@/contexts/AuthContext';
import { GoogleDriveService } from '@/modules/QMS/services/driveService';

export default function GDriveAutoMapModal({ isOpen, onClose, companyId, onComplete }) {
    const { user } = useAuth();
    const [step, setStep] = useState(1);
    
    // Step 1: Input
    const [rootUrl, setRootUrl] = useState('');
    const [isFetching, setIsFetching] = useState(false);

    // Step 2: Mapping Results
    const [matchedUsers, setMatchedUsers] = useState([]); // { user: UserData, folder: FolderData }
    const [unmatchedFolders, setUnmatchedFolders] = useState([]); // Folders that didn't match anyone
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setStep(1);
            setRootUrl('');
            setMatchedUsers([]);
            setUnmatchedFolders([]);
        }
    }, [isOpen]);

    const handleFetchAndMap = async () => {
        if (!rootUrl.trim()) return;
        setIsFetching(true);
        
        try {
            const rootId = GoogleDriveService.extractFolderId(rootUrl);
            if (!rootId) throw new Error("Invalid Google Drive URL. Could not extract folder ID.");

            // 1. Fetch Google Drive Subfolders
            const folders = await GoogleDriveService.fetchRepFolders(rootId);
            
            // 2. Fetch all app users
            const usersSnapshot = await getDocs(collection(db, 'companies', companyId, 'app_users'));
            const appUsers = usersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

            // 3. Fuzzy Match Algorithm
            const matches = [];
            const unmatched = [];
            
            // Track users who already got matched to avoid duplicates
            const matchedUserIds = new Set();

            folders.forEach(folder => {
                const folderNameNorm = folder.name.toLowerCase().replace(/[^a-z0-9]/g, '');
                
                // Find matching user
                const matchedUser = appUsers.find(u => {
                    if (matchedUserIds.has(u.id)) return false; // Already matched
                    
                    const nameNorm = (u.primaryName || '').toLowerCase().replace(/[^a-z0-9]/g, '');
                    if (nameNorm && folderNameNorm.includes(nameNorm)) return true;
                    if (nameNorm && nameNorm.includes(folderNameNorm)) return true;
                    
                    // Check aliases
                    if (u.aliases && Array.isArray(u.aliases)) {
                        return u.aliases.some(alias => {
                            const aliasNorm = alias.toLowerCase().replace(/[^a-z0-9]/g, '');
                            return aliasNorm && (folderNameNorm.includes(aliasNorm) || aliasNorm.includes(folderNameNorm));
                        });
                    }
                    return false;
                });

                if (matchedUser) {
                    matches.push({ user: matchedUser, folder });
                    matchedUserIds.add(matchedUser.id);
                } else {
                    unmatched.push(folder);
                }
            });

            setMatchedUsers(matches);
            setUnmatchedFolders(unmatched);
            setStep(2);
        } catch (error) {
            logger.error("Auto-mapping failed:", error);
            alert(`Failed: ${error.message}`);
        } finally {
            setIsFetching(false);
        }
    };

    const handleSaveMapping = async () => {
        if (!companyId || matchedUsers.length === 0) return;
        setIsSaving(true);
        
        try {
            const promises = matchedUsers.map(match => {
                const userRef = doc(db, 'companies', companyId, 'app_users', match.user.id);
                const folderUrl = match.folder.webViewLink || `https://drive.google.com/drive/folders/${match.folder.id}`;
                return updateDoc(userRef, {
                    googleDriveLink: folderUrl
                });
            });

            await Promise.all(promises);
            
            if (onComplete) onComplete();
            onClose();
        } catch (error) {
            logger.error("Failed to save mappings:", error);
            alert("Failed to save mappings. Please try again.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div style={{
            position: 'fixed', inset: 0,
            background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 9999,
            display: 'flex', alignItems: 'center', justifyContent: 'center'
        }} className="animate-fade-in" onClick={onClose}>
            
            <motion.div 
                initial={{ opacity: 0, scale: 0.95, y: 20 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }} 
                exit={{ opacity: 0, scale: 0.95, y: 20 }} 
                onClick={e => e.stopPropagation()}
                style={{ 
                    width: '100%', maxWidth: '640px', background: 'var(--bg-surface)', 
                    borderRadius: '24px', position: 'relative', zIndex: 1, overflow: 'hidden', 
                    boxShadow: '0 24px 48px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column',
                    maxHeight: '90vh'
                }}
            >
                <div style={{ padding: '24px 32px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'var(--bg-page)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#3b82f620', border: '1px solid #3b82f640', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#3b82f6' }}>
                            <ObliqueIcon name="auto_awesome_mosaic" size={20} />
                        </div>
                        <div>
                            <h2 style={{ fontSize: '18px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>Auto-Map Google Drive Folders</h2>
                            <p style={{ fontSize: '13px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Sync parent folder and auto-match user subfolders.</p>
                        </div>
                    </div>
                    <button onClick={onClose} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: '8px' }} className="hover-bg-subtle" style={{ borderRadius: '8px' }}>
                        <ObliqueIcon name="close" size={20} />
                    </button>
                </div>

                <div style={{ padding: '32px', flex: 1, overflowY: 'auto' }} className="custom-scrollbar">
                    {step === 1 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div>
                                <label style={{ display: 'block', fontSize: '12px', fontWeight: '600', color: 'var(--text-secondary)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '8px' }}>Root Folder URL</label>
                                <input 
                                    value={rootUrl} 
                                    onChange={e => setRootUrl(e.target.value)} 
                                    style={{ width: '100%', padding: '12px 16px', borderRadius: '10px', border: '1px solid var(--border)', background: 'var(--bg-subtle)', color: 'var(--text-primary)', fontSize: '13px', outline: 'none' }}
                                    placeholder="https://drive.google.com/drive/folders/..." 
                                />
                                <p style={{ fontSize: '12px', color: 'var(--text-tertiary)', marginTop: '8px', lineHeight: '1.5' }}>
                                    Paste the URL to the parent folder containing the user subfolders (e.g., "SEO-Sales | Meetings"). We will scan this folder and match subfolders to your existing users based on their names and aliases.
                                </p>
                            </div>

                            <div style={{ padding: '16px', background: '#3b82f610', borderRadius: '12px', border: '1px solid #3b82f630', display: 'flex', gap: '12px' }}>
                                <ObliqueIcon name="info" size={20} color="#3b82f6" style={{ flexShrink: 0 }} />
                                <div style={{ fontSize: '13px', color: 'var(--text-primary)', lineHeight: '1.5' }}>
                                    <strong>How mapping works:</strong> The system looks at each subfolder's name and attempts to find a fuzzy match against a user's Primary Name or their configured Aliases. Only matching folders will be assigned.
                                </div>
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', padding: '16px', background: 'var(--bg-subtle)', borderRadius: '12px', border: '1px solid var(--border)' }}>
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{matchedUsers.length}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '500' }}>Successful Matches</div>
                                </div>
                                <div style={{ width: '1px', height: '40px', background: 'var(--border)' }} />
                                <div style={{ flex: 1 }}>
                                    <div style={{ fontSize: '24px', fontWeight: '700', color: 'var(--text-primary)' }}>{unmatchedFolders.length}</div>
                                    <div style={{ fontSize: '12px', color: 'var(--text-secondary)', fontWeight: '500' }}>Unmatched Folders</div>
                                </div>
                            </div>

                            {matchedUsers.length > 0 && (
                                <div>
                                    <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Matched Users</h3>
                                    <div style={{ border: '1px solid var(--border)', borderRadius: '12px', overflow: 'hidden' }}>
                                        {matchedUsers.map((m, idx) => (
                                            <div key={idx} style={{ display: 'flex', alignItems: 'center', padding: '12px 16px', borderBottom: idx < matchedUsers.length - 1 ? '1px solid var(--border)' : 'none', background: 'var(--bg-surface)' }}>
                                                <div style={{ flex: 1 }}>
                                                    <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-primary)' }}>{m.user.primaryName}</div>
                                                    <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{m.user.email}</div>
                                                </div>
                                                <ObliqueIcon name="link" size={16} color="var(--text-tertiary)" style={{ margin: '0 12px' }} />
                                                <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                    <ObliqueIcon name="folder" size={16} color="#3b82f6" />
                                                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.folder.name}</div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {unmatchedFolders.length > 0 && (
                                <div>
                                    <h3 style={{ fontSize: '13px', fontWeight: '600', color: 'var(--text-tertiary)', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Unmatched Folders (Ignored)</h3>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                        {unmatchedFolders.map((f, idx) => (
                                            <div key={idx} style={{ padding: '6px 12px', background: 'var(--bg-muted)', borderRadius: '100px', fontSize: '11px', fontWeight: '500', color: 'var(--text-secondary)' }}>
                                                <ObliqueIcon name="folder_off" size={12} style={{ display: 'inline', marginRight: '4px', verticalAlign: 'middle' }} />
                                                {f.name}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div style={{ padding: '20px 32px', borderTop: '1px solid var(--border)', background: 'var(--bg-page)', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                    <button type="button" onClick={onClose} className="btn btn-secondary" style={{ padding: '10px 20px', borderRadius: '10px', background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-secondary)', fontWeight: '500', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                    {step === 1 ? (
                        <button type="button" onClick={handleFetchAndMap} className="btn btn-primary" disabled={isFetching || !rootUrl} style={{ padding: '10px 24px', borderRadius: '10px', background: 'var(--text-primary)', border: 'none', color: 'var(--bg-surface)', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                            {isFetching ? <Spinner size={16} color="var(--bg-surface)" /> : 'Scan & Match'}
                        </button>
                    ) : (
                        <button type="button" onClick={handleSaveMapping} className="btn btn-primary" disabled={isSaving || matchedUsers.length === 0} style={{ padding: '10px 24px', borderRadius: '10px', background: '#3b82f6', border: 'none', color: '#fff', fontWeight: '600', fontSize: '13px', cursor: 'pointer' }}>
                            {isSaving ? <Spinner size={16} color="#fff" /> : 'Save Mappings'}
                        </button>
                    )}
                </div>
            </motion.div>
        </div>
    );
}
